// =============================
// CART DRAWER (Clearflow — Final Stable Version)
// =============================

/**
 * Get the currency currently selected in the Bucks CC widget.
 * Tries window.bucksCC API first, then DOM fallback. Returns null if unknown.
 */
function getBucksSelectedCurrency() {
  let source = 'none';
  if (window.bucksCC) {
    if (typeof window.bucksCC.getSelectedCurrency === 'function') {
      const code = window.bucksCC.getSelectedCurrency();
      if (code && typeof code === 'string') {
        source = 'getSelectedCurrency';
        return code.trim();
      }
    }
    if (typeof window.bucksCC.currentCurrency === 'string' && window.bucksCC.currentCurrency) {
      source = 'currentCurrency';
      return window.bucksCC.currentCurrency.trim();
    }
    if (typeof window.bucksCC.selectedCurrency === 'string' && window.bucksCC.selectedCurrency) {
      source = 'selectedCurrency';
      return window.bucksCC.selectedCurrency.trim();
    }
    // Bucks API shape from runtime: config, getConfig, localization, Currency, widget, etc.
    try {
      const cfg = typeof window.bucksCC.getConfig === 'function' ? window.bucksCC.getConfig() : window.bucksCC.config;
      if (cfg && typeof cfg === 'object') {
        const code = (cfg.userCurrency || cfg.selectedCurrency || cfg.currency || cfg.iso_code || (cfg.currency && cfg.currency.iso_code));
        if (typeof code === 'string' && code.trim()) {
          source = 'config';
          return code.trim();
        }
        if (cfg.currency && typeof cfg.currency === 'object' && typeof cfg.currency.iso_code === 'string') {
          source = 'config.currency';
          return cfg.currency.iso_code.trim();
        }
      }
    } catch (_) {}
    try {
      const loc = window.bucksCC.localization;
      if (loc && typeof loc === 'object') {
        const code = (loc.currency && (typeof loc.currency === 'string' ? loc.currency : loc.currency.iso_code))
          || loc.selectedCurrency
          || loc.iso_code;
        if (typeof code === 'string' && code.trim()) {
          source = 'localization';
          return code.trim();
        }
      }
    } catch (_) {}
  }
  const el = document.querySelector('.bucks-selected [data-currency], .buckscc-select-styled [data-currency], .buckscc-currency-box [data-currency]');
  if (el && el.getAttribute('data-currency')) {
    source = 'dom';
    return el.getAttribute('data-currency').trim();
  }
  // #region agent log
  (function(){
    var cfg = window.bucksCC && typeof window.bucksCC.getConfig === 'function' ? window.bucksCC.getConfig() : (window.bucksCC && window.bucksCC.config);
    var loc = window.bucksCC && window.bucksCC.localization;
    var cfgKeys = cfg && typeof cfg === 'object' ? Object.keys(cfg) : [];
    var locKeys = loc && typeof loc === 'object' ? Object.keys(loc) : [];
    fetch('http://127.0.0.1:7416/ingest/88d7bcef-4751-4295-81de-f85e3005f8ab',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'11b3c9'},body:JSON.stringify({sessionId:'11b3c9',location:'cart-drawer.js:getBucksSelectedCurrency',message:'Bucks selected currency source',data:{source:source||'null',bucksCCKeys:window.bucksCC?Object.keys(window.bucksCC):[],configKeys:cfgKeys,localizationKeys:locKeys,configSample:cfg&&typeof cfg=== 'object'?JSON.stringify(cfg).slice(0,500):null},timestamp:Date.now(),hypothesisId:'B'})}).catch(function(){});
  })();
  // #endregion
  return null;
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Close on ESC key
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    // Overlay click closes
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    // Accessibility setup
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => this.classList.add('animate', 'active'), 10);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : this.querySelector('#CartDrawer');
        const focusElement =
          this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement)
          trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.id) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      const isOpen = event.currentTarget.closest('details').hasAttribute('open');
      event.currentTarget.setAttribute('aria-expanded', !isOpen);
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // =============================
  // MAIN RENDER LOGIC (with Empty Cart Fix)
  // =============================
  renderContents(parsedState) {
    if (!parsedState || !parsedState.sections) {
      console.warn('⚠️ Invalid parsedState passed to renderContents');
      return;
    }

    const storeCurrency = (this.getAttribute('data-store-currency') || '').trim() || null;
    const selectedCurrency = getBucksSelectedCurrency();
    const needsConversion = !storeCurrency || !selectedCurrency ||
      storeCurrency.toUpperCase() !== selectedCurrency.toUpperCase();
    this._bucksNeedsConversion = needsConversion;
    // #region agent log
    fetch('http://127.0.0.1:7416/ingest/88d7bcef-4751-4295-81de-f85e3005f8ab',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'11b3c9'},body:JSON.stringify({sessionId:'11b3c9',location:'cart-drawer.js:renderContents',message:'Bucks currency decision',data:{storeCurrency,selectedCurrency,needsConversion,action:needsConversion?'set data-bucks-pending':'remove data-bucks-pending',hasBucksCC:!!window.bucksCC},timestamp:Date.now(),hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    if (needsConversion) {
      this.setAttribute('data-bucks-pending', '');
    } else {
      this.removeAttribute('data-bucks-pending');
    }

    const cartHTML = parsedState.sections['cart-drawer'] || '';
    const isEmpty =
      cartHTML.includes('cart__empty-text') ||
      cartHTML.includes('Your cart is empty');

    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections?.[section.id];
      if (!html) return;

      const decodedHTML = this.decodeAndExtract(html, section.selector);
      if (!decodedHTML) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (sectionElement) {
        sectionElement.innerHTML = decodedHTML;
        console.log(`✅ Updated section: ${section.id}`);
        // Trigger Bucks currency converter on new content to avoid price flash
        if (section.id === 'cart-drawer' && window.bucksCC && typeof window.bucksCC.reConvert === 'function') {
          requestAnimationFrame(() => {
            window.bucksCC.reConvert();
          });
        }
      } else {
        console.warn(`⚠️ Target not found for section: ${section.id}`);
      }
    });

    // Handle empty cart display logic
    this.classList.toggle('is-empty', isEmpty);

setTimeout(() => {
  const overlay = this.querySelector('#CartDrawer-Overlay');
  if (overlay) overlay.addEventListener('click', this.close.bind(this));
  this.open();

  const emptyView = this.querySelector('.drawer__inner-empty');
  const form = this.querySelector('#CartDrawer-Form');

  if (isEmpty) {
    if (emptyView) emptyView.style.display = 'block';
    if (form) form.style.display = 'none';

    // 🧩 Fix for misaligned empty state (after removing last item)
    const innerWrapper = this.querySelector('.drawer__inner');
    if (emptyView && innerWrapper && !innerWrapper.contains(emptyView)) {
      innerWrapper.innerHTML = '';
      innerWrapper.appendChild(emptyView);
      console.log('🧩 Reattached empty state inside .drawer__inner for centering');
    }

    // Ensure it’s centered
    emptyView.style.display = 'flex';
    emptyView.style.alignItems = 'center';
    emptyView.style.justifyContent = 'center';
    emptyView.style.minHeight = '100%';

    console.log('🟢 Cart is empty — showing empty message');
  } else {
    if (emptyView) emptyView.style.display = 'none';
    if (form) form.style.display = 'block';
  }

  // Trigger Bucks re-conversion for newly injected cart prices (avoids store-currency flash)
  if (window.bucksCC && typeof window.bucksCC.reConvert === 'function') {
    window.bucksCC.reConvert();
  }
  // Fallback: show prices after delay if Bucks didn't convert (only when we hid them)
  const drawer = document.querySelector('cart-drawer');
  if (drawer && drawer._bucksNeedsConversion) {
    clearTimeout(drawer._bucksFallbackTimer);
    drawer._bucksFallbackTimer = setTimeout(() => {
      drawer.removeAttribute('data-bucks-pending');
    }, 800);
  }

  console.log('🟢 Cart drawer content rendered and reopened');
}, 0);

  }

  // =============================
  // SAFE DECODE + EXTRACTION
  // =============================
  decodeAndExtract(html, selector) {
    try {
      const temp = document.createElement('textarea');
      temp.innerHTML = html;
      const decoded = temp.value;

      const parser = new DOMParser();
      const doc = parser.parseFromString(decoded, 'text/html');

      const cartEl =
        doc.querySelector('#CartDrawer') ||
        doc.querySelector(selector) ||
        doc.body;

      if (!cartEl) {
        console.warn('⚠️ No matching element found for', selector);
        return '';
      }

      cartEl.querySelectorAll('script, style').forEach((el) => el.remove());

      return cartEl.innerHTML;
    } catch (err) {
      console.error('❌ decodeAndExtract failed:', err);
      return '';
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        selector: '#cart-icon-bubble',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// =============================
// FORCE-OVERRIDE CART DRAWER ITEMS HANDLER (422 Fix + Empty Cart Fix)
// =============================
if (customElements.get('cart-drawer-items')) {
  customElements.delete('cart-drawer-items');
}

class CartDrawerItems extends CartItems {
  async updateQuantity(line, quantity) {
    this.enableLoading(line);

    try {
      // ✅ Always use .js endpoint
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line,
          quantity,
          sections: this.getSectionsToRender().map((s) => s.section),
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) throw new Error(`Cart update failed (${response.status})`);

      const data = await response.json();
      const cartDrawer = document.querySelector('cart-drawer');

      // ✅ Decode Shopify escaped HTML safely
      const parsedState = { sections: {} };
      Object.keys(data.sections || {}).forEach((key) => {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = data.sections[key];
        const decoded = textarea.value;

        const parser = new DOMParser();
        const doc = parser.parseFromString(decoded, 'text/html');
        const sectionEl =
          doc.querySelector('#CartDrawer') ||
          doc.querySelector('.cart-drawer') ||
          doc.body;

        sectionEl.querySelectorAll('script,style').forEach((el) => el.remove());
        parsedState.sections[key] = sectionEl.innerHTML.trim();
      });

      cartDrawer.renderContents(parsedState);
      this.disableLoading(line);
      console.log('🟢 Cart item quantity updated successfully');
    } catch (err) {
      console.error('❌ Error updating cart:', err);
      const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      if (errors) {
        errors.textContent = 'There was an error while updating your cart. Please try again.';
        errors.style.display = 'block';
        setTimeout(() => (errors.style.display = 'none'), 3000);
      }
      this.disableLoading(line);
    }
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', section: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '#cart-icon-bubble' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
